import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accessApi, type Permission } from '@/api/access-api';
import { SearchInput } from '@/acc-ui/primitives/SearchInput';
import { HighlightText } from '@/acc-ui/primitives/HighlightText';
import { Card, SectionLabel, ErrorState } from '@/acc-ui/primitives/Layout';
import { Button } from '@/acc-ui/primitives/Button';

export function AccessPermissionsTab() {
  const [search, setSearch] = useState('');
  const permsQuery = useQuery({ queryKey: ['permissions'], queryFn: accessApi.listPermissions });

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (p: Permission) =>
      !q ||
      p.key.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.module.toLowerCase().includes(q);

    const perms = (permsQuery.data ?? []).filter(match);
    const g = new Map<string, Permission[]>();
    for (const p of perms) {
      const list = g.get(p.module) ?? [];
      list.push(p);
      g.set(p.module, list);
    }
    return Array.from(g.entries())
      .map(([m, ps]) => [m, ps.sort((a, b) => a.action.localeCompare(b.action))] as const)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [permsQuery.data, search]);

  if (permsQuery.isError) {
    return (
      <ErrorState
        description="Could not load permissions."
        action={<Button onClick={() => permsQuery.refetch()}>Retry</Button>}
      />
    );
  }

  const total = permsQuery.data?.length ?? 0;
  const shown = grouped.reduce((n, [, ps]) => n + ps.length, 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex-1 max-w-md">
          <SearchInput
            placeholder="Search by module, action, or description…"
            value={search}
            onChange={setSearch}
          />
        </div>
        <span className="text-caption text-text-tertiary tabular-nums">
          {search ? `${shown} of ${total}` : `${total} permissions`}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {grouped.map(([moduleName, perms]) => (
          <Card key={moduleName} padding={false}>
            <div className="px-4 py-2.5 border-b border-glass-hairline">
              <SectionLabel>
                {moduleName}{' '}
                <span className="normal-case text-text-tertiary font-medium">
                  ({perms.length})
                </span>
              </SectionLabel>
            </div>
            <ul className="divide-y divide-glass-hairline">
              {perms.map((p) => (
                <li key={p.key} className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-label font-semibold text-text-primary">
                      <HighlightText text={p.action} query={search} />
                    </span>
                    <code className="font-mono text-micro text-text-muted bg-slate-900/5 rounded px-1.5 py-0.5">
                      <HighlightText text={p.key} query={search} />
                    </code>
                  </div>
                  <p className="text-caption text-text-tertiary mt-0.5">
                    <HighlightText text={p.description} query={search} />
                  </p>
                </li>
              ))}
            </ul>
          </Card>
        ))}
        {grouped.length === 0 && (
          <div className="col-span-full text-label text-text-tertiary text-center py-8">
            No permissions match “{search}”.
          </div>
        )}
      </div>
    </div>
  );
}
